import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { subscriptionApi } from '../api/subscription.api';

export default function UpgradePlanDialog({ open, onClose, reason = 'limit' }) {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      setLoading(true);
      subscriptionApi
        .getPlans()
        .then((res) => setPlans(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open]);

  const handleUpgrade = () => {
    if (onClose) onClose();
    navigate('/select-plan');
  };

  const proPlans = plans.filter((p) => p.priceMonthly > 0 || (!p.name?.toLowerCase().includes('free') && p.priceMonthly !== 0));

  const title =
    reason === 'automation'
      ? 'Upgrade to Enable Automation'
      : reason === 'limit'
      ? 'Free SMS Limit Reached'
      : reason === 'feature'
      ? 'This Feature Requires a Paid Plan'
      : 'Upgrade Your Plan';

  const subtitle =
    reason === 'automation'
      ? 'Automation workflows require a Pro plan. Upgrade to unlock unlimited workflows and pay-as-you-go SMS.'
      : reason === 'feature'
      ? 'Upgrade to unlock widgets, funnels, QR codes and automation workflows.'
      : "You've used all your free SMS credits. Upgrade to Pro for pay-as-you-go SMS and unlimited workflows.";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <RocketLaunchIcon color="primary" />
        <Box>
          <Typography variant="h6" fontWeight={600}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            {proPlans.map((plan) => (
              <Box
                key={plan.id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h6" fontWeight={600}>{plan.name}</Typography>
                      <Chip label="Pro" size="small" color="primary" />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      ${plan.priceMonthly}/{(plan.billingCycle || 'monthly') === 'yearly' ? 'year' : 'month'} + ${plan.smsRate || '0.10'}/SMS
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ borderRadius: 2, px: 3 }}
                    onClick={handleUpgrade}
                  >
                    Choose Plan
                  </Button>
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <FeatureItem text="Unlimited workflows" />
                  <FeatureItem text="Pay-as-you-go SMS" />
                  <FeatureItem text="Auto-reload credits" />
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Maybe Later
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function FeatureItem({ text }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
      <Typography variant="caption">{text}</Typography>
    </Stack>
  );
}
